import { Platform, Pressable, StyleSheet, Text as RNText, View } from "@-/com.npmjs.react-native";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  getProgressMeterFillColor,
  ProgressMeter,
} from "@-/dev.improve.haniverse.components.ui.progress-meter";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { ReactNode } from "react";
import { playOnboardingFeedback } from "#play-onboarding-feedback";

/**
 * The onboarding's one action per page: a full-width pill as tall as the toolbar's buttons, solid
 * in the theme's ink, like the welcome galaxy's Get Started. Disabled until the page's question
 * has an answer. A button that takes the learner somewhere ends with that place's icon.
 */
export function OnboardingContinueButton({
  disabled = false,
  icon,
  label,
  onPress,
  testID,
}: {
  disabled?: boolean;
  icon?: IconName;
  label: string;
  onPress: () => void;
  testID: string;
}) {
  const { diameter } = useToolbarGridMetrics();
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={`flex-row items-center justify-center gap-2 rounded-full ${
        disabled ? "bg-muted" : "bg-foreground active:opacity-80"
      }`}
      disabled={disabled}
      onPress={() => {
        playOnboardingFeedback("confirm");
        onPress();
      }}
      style={{ height: diameter + 6 }}
      testID={testID}
    >
      <Text
        className={`text-base font-semibold ${
          disabled ? "text-muted-foreground" : "text-background"
        }`}
      >
        {label}
      </Text>
      {icon === undefined ? null : (
        <Icon
          className={disabled ? "text-muted-foreground" : "text-background"}
          name={icon}
          size={20}
        />
      )}
    </Pressable>
  );
}

/**
 * An emoji drawn in the system's colour emoji font: the app's own font has monochrome forms of
 * some of them (✈, ⏰), which would read as plain glyphs.
 */
export function OnboardingEmoji({ children, size }: { children: string; size: number }) {
  return (
    <RNText
      accessibilityElementsHidden
      className="text-center"
      importantForAccessibility="no"
      style={[
        Platform.OS === "android" ? styles.androidSystemEmoji : undefined,
        { fontSize: size, lineHeight: size * 1.25, width: size * 1.3 },
      ]}
    >
      {children}
    </RNText>
  );
}

const styles = StyleSheet.create({
  // The app theme installs Han Composite as Android's default native text face. Raw RN Text alone
  // therefore still selected its monochrome fallback for most survey emoji on Pixel; explicitly
  // restore Android's system cascade so Noto Color Emoji owns these glyphs.
  androidSystemEmoji: { fontFamily: "sans-serif" },
});

/** One answer to a question: a row that marks itself chosen. */
export function OnboardingChoice({
  isMultiple = false,
  label,
  leading,
  onPress,
  selected,
  testID,
  trailing,
}: {
  /** Several answers may be chosen (a checkbox), rather than one (a radio). */
  isMultiple?: boolean;
  label: string;
  leading?: ReactNode;
  onPress: () => void;
  selected: boolean;
  testID: string;
  trailing?: string;
}) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole={isMultiple ? "checkbox" : "radio"}
      accessibilityState={isMultiple ? { checked: selected } : { selected }}
      className={`min-h-14 flex-row items-center gap-3 rounded-xs border-2 px-4 py-3 active:opacity-80 ${
        selected ? "border-primary bg-primary/10" : "border-border bg-surface"
      }`}
      onPress={() => {
        playOnboardingFeedback("select");
        onPress();
      }}
      testID={testID}
    >
      {leading}
      <Text
        className={`flex-1 text-lg font-semibold ${selected ? "text-primary" : "text-foreground"}`}
      >
        {label}
      </Text>
      {trailing === undefined ? null : (
        <Text className="text-base text-muted-foreground">{trailing}</Text>
      )}
      {isMultiple ? (
        <View
          className={`size-6 items-center justify-center rounded-xs border-2 ${
            selected ? "border-primary bg-primary" : "border-border"
          }`}
        >
          {selected ? <Icon className="text-primary-foreground" name="MdCheck" size={16} /> : null}
        </View>
      ) : null}
    </Pressable>
  );
}

/**
 * How far through the questions the learner is: the review recharge bar's meter
 * (`ProgressMeter`), filling gold as they go.
 */
export function OnboardingProgressBar({ progress }: { progress: number }) {
  return (
    <View
      accessibilityLabel={`${Math.round(progress * 100)}% done`}
      accessibilityRole="progressbar"
      className="h-5.5 flex-1"
      testID="onboarding-progress-bar"
    >
      <ProgressMeter
        fillColor={getProgressMeterFillColor({ fraction: progress, isSpending: false })}
        fillTestID="onboarding-progress-fill"
        fraction={progress}
        testID="onboarding-progress-meter"
      />
    </View>
  );
}

/**
 * Back to the previous page of the onboarding: the app shell's back control, a glass circle with
 * a chevron the size of the toolbar's buttons.
 */
export function OnboardingBackButton({ onPress }: { onPress: () => void }) {
  const { diameter } = useToolbarGridMetrics();
  return (
    <ToolbarGlassSurface style={{ height: diameter, width: diameter }}>
      <Pressable
        accessibilityLabel="Back"
        accessibilityRole="button"
        className="size-full items-center justify-center"
        hitSlop={8}
        onPress={onPress}
        testID="onboarding-back-button"
      >
        <Icon className="text-foreground" name="FiChevronLeftBold" size={24} />
      </Pressable>
    </ToolbarGlassSurface>
  );
}
